/**
 * Onboarding feature — network bindings.
 *
 * Two mounts of the SAME backend handler:
 *
 *   PUBLIC:        POST /api/onboarding/v1/onboarding/recommended-default
 *   AUTHENTICATED: POST /api/me/v1/onboarding/recommended-default
 *
 * Both TENANT-scoped via host; same response shape. The
 * authenticated mount runs through SessionAuth so the handler
 * sees a non-nil xhttp.UserID(ctx) and the recommender's layer-5
 * frontline routing fires for frontline-marked users.
 *
 * Pre-Identify callers (tenant login surface) use the public
 * mount; post-Identify callers (step-up dialog) opt into the
 * authenticated mount via `useRecommendedDefault({ authed: true })`.
 *
 * # Why POST for a read
 *
 * Matches the coach pattern (POST /me/coach/recommend). POST
 * keeps the response out of GET caches so a freshly-loaded chip
 * always reflects the current device class + tenant policy.
 *
 * # Always-recommend contract
 *
 * The backend never returns 5xx on this endpoint — it falls
 * through to a "fallback" provider on internal errors. The hook
 * surfaces the recommendation regardless; consumers can render
 * the chip immediately on data arrival.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  parseUaHint,
  recommendedDefaultResponseSchema,
  type RecommendedDefaultRequest,
  type UaHint,
} from "@pureid/auth-core";
import type { RecommendedDefault } from "@pureid/ui";

import { apiFetch } from "@/lib/api-client";

const ENDPOINT_PUBLIC = "/api/onboarding/v1/onboarding/recommended-default";
const ENDPOINT_AUTHED = "/api/me/v1/onboarding/recommended-default";

/**
 * fetchRecommendedDefault calls the backend recommender with an
 * optional UA hint (so the chip can show "Mac with Touch ID"
 * instead of "This device") and normalises the wire snake_case
 * to the UI camelCase. Returns a `RecommendedDefault` shaped
 * exactly like the `@pureid/ui/onboarding-slots` type so the chip
 * + downstream consumers don't need to re-translate.
 */
export async function fetchRecommendedDefault(
  ua?: UaHint | null,
  opts?: { authed?: boolean },
): Promise<RecommendedDefault> {
  // We send the ua_hint whenever ANY field (os or browser) is
  // present and useful. Sending `browser` without `os` is fine —
  // the server tolerates partial hints and the DD-9 rule only
  // fires when both reach "Linux" + "Firefox" together. Empty /
  // "Unknown" values are stripped so the server's vocabulary
  // doesn't have to special-case sentinels (it already does for
  // missing fields).
  const body: RecommendedDefaultRequest | undefined = (() => {
    if (!ua) return undefined;
    const os = ua.os && ua.os !== "Unknown" ? ua.os : "";
    const browser = ua.browser && ua.browser !== "Unknown" ? ua.browser : "";
    if (!os && !browser) return undefined;
    const hint: { os: string; browser?: string } = { os };
    if (browser) hint.browser = browser;
    return { ua_hint: hint };
  })();
  const endpoint = opts?.authed ? ENDPOINT_AUTHED : ENDPOINT_PUBLIC;
  const raw = await apiFetch<unknown>(endpoint, {
    method: "POST",
    body,
  });
  const parsed = recommendedDefaultResponseSchema.parse(raw);
  return {
    deviceHintSlot: parsed.device_hint_slot as RecommendedDefault["deviceHintSlot"],
    method: parsed.method,
    reasonSlot: parsed.reason_slot as RecommendedDefault["reasonSlot"],
    alternatives: parsed.alternatives.map((a) => ({
      method: a.method,
      reasonSlot: a.reason_slot as RecommendedDefault["reasonSlot"],
    })),
    provider: parsed.provider,
  };
}

const recommendedDefaultQueryKey = (
  osHint: string | null,
  browserHint: string | null,
  authed: boolean,
) =>
  [
    "onboarding",
    "recommended-default",
    authed ? "authed" : "public",
    osHint ?? "",
    browserHint ?? "",
  ] as const;

/**
 * useRecommendedDefault subscribes to the current page's
 * recommendation. Reads the client's UA hint via `parseUaHint`
 * and passes it along so the server's recommender can refine
 * `device_hint.unknown` to an OS-specific slot
 * (Mac/Windows/iPhone/Android/Chromebook/Linux).
 *
 * Stale-time is long (15 min) — the underlying device class
 * + OS don't change mid-session and the chip should not flicker
 * when adjacent state updates.
 *
 * Errors are swallowed (no toast, no error UI): if the chip
 * can't render, it should simply be absent rather than dim the
 * surface with an error tile. The chip component returns null
 * when given undefined.
 *
 * # `authed` parameter (F-fix)
 *
 * Defaults to `false` (public endpoint). Pre-Identify callers
 * (tenant login surface) keep the default. Post-Identify
 * callers (admin step-up dialog) pass `true` so the request
 * runs through SessionAuth and the recommender's layer-5
 * frontline routing fires for frontline-marked admins.
 *
 * Query-key includes the authed flag so the cache distinguishes
 * the two responses.
 */
export function useRecommendedDefault(opts?: { authed?: boolean }) {
  const authed = opts?.authed ?? false;
  const ua =
    typeof navigator !== "undefined" ? parseUaHint(navigator.userAgent) : null;
  return useQuery({
    queryKey: recommendedDefaultQueryKey(
      ua?.os ?? null,
      ua?.browser ?? null,
      authed,
    ),
    queryFn: () => fetchRecommendedDefault(ua, { authed }),
    staleTime: 15 * 60 * 1000,
    retry: false,
    // Errors aren't actionable for the user — the chip just
    // doesn't render. Suppress them at the query-cache level.
    throwOnError: false,
  });
}

// ── DD-5 v2 — audit emission on chip render ────────────────────────

/**
 * useReportRecommendationShown fires the
 * `onboarding.recommendation_shown` audit emit ONCE per
 * (token, recommendation) pair. The backend endpoint
 * (`POST /api/admin-enroll/v1/{token}/audit/recommendation-shown`)
 * resolves the token to the admin user and emits the row
 * with actor=user. Charter N4 satisfied.
 *
 * # Why a fire-and-forget ref + useEffect
 *
 * The emit is observational, not user-blocking. We don't
 * wait on it, don't toast on error, don't retry. A ref
 * gates the emit to exactly once per recommendation+token
 * pair so re-renders (and even page revisits within the
 * 15-min staleTime window) don't fire duplicate rows.
 *
 * The hook is a no-op when:
 *   - `token` is empty (the wizard hasn't bound to a token
 *     yet — likely the validating/invalid step)
 *   - `recommendation` is undefined (still loading or the
 *     fetch failed)
 */
export function useReportRecommendationShown(
  token: string | undefined,
  recommendation: RecommendedDefault | undefined,
): void {
  const sentKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token || !recommendation) return;
    // Compose a stable key for "this token + this exact
    // recommendation." If the recommendation changes mid-
    // session (rare; only on staleTime expiry + refetch
    // returning different data), we re-fire so the audit
    // trail captures the new state.
    const key = JSON.stringify({
      t: token,
      m: recommendation.method,
      d: recommendation.deviceHintSlot,
      r: recommendation.reasonSlot,
      p: recommendation.provider,
    });
    if (sentKeyRef.current === key) return;
    sentKeyRef.current = key;

    const body = {
      device_class: deviceClassFromRecommendation(recommendation),
      playbook_id: "",
      recommended_method: recommendation.method,
      device_hint_slot: recommendation.deviceHintSlot,
      reason_slot: recommendation.reasonSlot,
      alternative_methods: recommendation.alternatives.map((a) => a.method),
      has_outstanding_tap: false,
      provider: recommendation.provider,
    };
    const url = `/api/admin-enroll/v1/${encodeURIComponent(token)}/audit/recommendation-shown`;
    void apiFetch<unknown>(url, { method: "POST", body }).catch(() => {
      // Audit emit is fire-and-forget; the user-facing
      // experience doesn't depend on it. A failure here
      // means SOC misses one row; nothing else breaks.
    });
  }, [token, recommendation]);
}

/**
 * reportRecommendationEscalated fires the DD-5 v2
 * `onboarding.recommendation_escalated` audit emit when the
 * wizard's in-flow failure escalation actually advances the
 * user from the original recommendation to one of the
 * alternatives.
 *
 * # Why an imperative function (not a hook)
 *
 * The shown-event is "fire once when this resource exists"
 * shaped — a useEffect is the natural fit. The escalated-event
 * is "fire NOW because the user just did a thing" shaped — an
 * event handler / dispatch-side imperative call reads more
 * cleanly than another useEffect chained off escalationCount.
 *
 * # Charter parity
 *
 * The body keys mirror the
 * RecommendationEscalatedInput struct in
 * internal/admin/enrollment/recommendation_audit.go, which in
 * turn mirrors the charter fixture's Metadata map for the
 * `onboarding.recommendation_escalated` action. Drift is
 * caught by the catalog drift test at CI time.
 *
 * # Failure mode
 *
 * Fire-and-forget. We swallow every error so the user-facing
 * escalation UX doesn't depend on the audit pipeline being
 * available.
 */
export function reportRecommendationEscalated(
  token: string | undefined,
  recommendation: RecommendedDefault | undefined,
  args: {
    originalMethod: string;
    escalatedToMethod: string;
    escalationCount: number;
  },
): void {
  if (!token || !recommendation) return;
  const body = {
    device_class: deviceClassFromRecommendation(recommendation),
    playbook_id: "",
    original_method: args.originalMethod,
    escalated_to_method: args.escalatedToMethod,
    device_hint_slot: recommendation.deviceHintSlot,
    escalation_count: args.escalationCount,
    provider: recommendation.provider,
  };
  const url = `/api/admin-enroll/v1/${encodeURIComponent(token)}/audit/recommendation-escalated`;
  void apiFetch<unknown>(url, { method: "POST", body }).catch(() => {
    // Audit emit is fire-and-forget; same rationale as the
    // shown hook. A failure here means SOC misses one row;
    // the wizard's escalation flow proceeds regardless.
  });
}

// deviceClassFromRecommendation infers the server-side device
// class from the device_hint_slot the recommender returned.
// The slot vocabulary is the only signal we get on the wire;
// the backend's recommender owns the deviceclass.Class enum
// privately. This best-effort mapping covers the 9 canonical
// classes via their hint slots; unknowns route to "unknown."
function deviceClassFromRecommendation(rec: RecommendedDefault): string {
  switch (rec.deviceHintSlot) {
    case "device_hint.azure_vm":
      return "fragile_vm";
    case "device_hint.vdi":
      return "vdi_class";
    case "device_hint.kiosk":
      return "shared_or_kiosk";
    case "device_hint.private_mode":
      return "private_mode";
    case "device_hint.insecure_context":
      return "insecure_context";
    case "device_hint.no_webauthn":
      return "no_webauthn";
    case "device_hint.mac":
    case "device_hint.windows":
    case "device_hint.iphone":
    case "device_hint.android":
    case "device_hint.chromeos":
      return "modern_capable";
    case "device_hint.linux":
      // Linux without TPM lands in fragile_partial; Linux
      // with TPM lands in modern_capable. The slot key alone
      // can't distinguish; pick the more common (TPM-less)
      // since DD-1's fragile_partial branch is what surfaced
      // the Linux hint.
      return "fragile_partial";
    case "device_hint.unknown":
    default:
      return "unknown";
  }
}
